/**
 * Sports Data Transformer
 * Maps new sportsmain API response format → existing component format
 *
 * New API section[].odds[] structure:
 *   { odds, otype: "back"|"lay", oname: "back1"|"lay1", size, tno }
 *
 * Existing component format:
 *   { one: { back: { price, size }, lay: { price, size } }, x: {...}, two: {...} }
 */

import { SID_TO_NAME } from './sportsConfig';

/**
 * Parse "Team A v Team B" from ename
 */
const parseTeams = (ename) => {
  if (!ename) return { team1: '', team2: '' };
  // Try splitting on " v " first, then " - "
  let parts = ename.split(' v ');
  if (parts.length >= 2) {
    return { team1: parts[0].trim(), team2: parts.slice(1).join(' v ').trim() };
  }
  parts = ename.split(' - ');
  if (parts.length >= 2) {
    return { team1: parts[0].trim(), team2: parts.slice(1).join(' - ').trim() };
  }
  return { team1: ename, team2: '' };
};

/**
 * Extract back1/lay1 odds for a single section runner
 */
const extractOdds = (odds) => {
  if (!Array.isArray(odds)) return { back: { price: '', size: 0 }, lay: { price: '', size: 0 } };

  let back = { price: '', size: 0 };
  let lay = { price: '', size: 0 };

  for (const o of odds) {
    if (o.oname === 'back1' && o.odds > 0) {
      back = { price: o.odds, size: o.size || 0 };
    } else if (o.oname === 'lay1' && o.odds > 0) {
      lay = { price: o.odds, size: o.size || 0 };
    }
  }

  return { back, lay };
};

/**
 * Extract back1/2/3 and lay1/2/3 odds for detailed view
 */
const extractDetailedOdds = (odds) => {
  if (!Array.isArray(odds)) {
    return {
      backOdds: [{ value: '--', volume: 0 }, { value: '--', volume: 0 }, { value: '--', volume: 0 }],
      layOdds: [{ value: '--', volume: 0 }, { value: '--', volume: 0 }, { value: '--', volume: 0 }],
    };
  }

  const backMap = {};
  const layMap = {};

  for (const o of odds) {
    if (o.otype === 'back') {
      backMap[o.oname] = { value: o.odds > 0 ? o.odds : '--', volume: o.size || 0 };
    } else if (o.otype === 'lay') {
      layMap[o.oname] = { value: o.odds > 0 ? o.odds : '--', volume: o.size || 0 };
    }
  }

  const backOdds = [
    backMap['back3'] || { value: '--', volume: 0 },
    backMap['back2'] || { value: '--', volume: 0 },
    backMap['back1'] || { value: '--', volume: 0 },
  ];

  const layOdds = [
    layMap['lay1'] || { value: '--', volume: 0 },
    layMap['lay2'] || { value: '--', volume: 0 },
    layMap['lay3'] || { value: '--', volume: 0 },
  ];

  return { backOdds, layOdds };
};

/**
 * Parse stime "3/1/2026 1:30:00 PM" to Date
 */
const parseStime = (stime) => {
  if (!stime) return new Date();
  try {
    return new Date(stime);
  } catch {
    return new Date();
  }
};

/**
 * Transform the get-all-sports-data API response to match list format
 * @param {Object} apiResponse - Raw API response
 * @param {number} sid - Sport ID
 * @returns {Array} Array of match objects matching existing component format
 */
/**
 * Find a section runner by sno value
 */
const findRunnerBySno = (section, sno) => section.find((s) => s.sno === sno);

/**
 * Transform a single match object into component format
 */
const transformMatch = (match, sid, sportName) => {
  const { team1, team2 } = parseTeams(match.ename);
  const section = match.section || [];
  const runnerCount = match.rc || section.length;

  // Build odds from section runners using sno for correct ordering
  // sno=1 → team1 (one), sno=2 → draw (x), sno=3 → team2 (two)
  const odds = {
    one: { back: { price: '', size: 0 }, lay: { price: '', size: 0 } },
    x: { back: { price: '', size: 0 }, lay: { price: '', size: 0 } },
    two: { back: { price: '', size: 0 }, lay: { price: '', size: 0 } },
    totalMatched: 0,
  };

  if (section.length >= 2) {
    const runner1 = findRunnerBySno(section, 1) || section[0];
    odds.one = extractOdds(runner1?.odds);

    if (runnerCount === 3 && section.length >= 3) {
      // 3-runner market (e.g. soccer with draw)
      const runnerX = findRunnerBySno(section, 2) || section[1];
      const runner2 = findRunnerBySno(section, 3) || section[2];
      odds.x = extractOdds(runnerX?.odds);
      odds.two = extractOdds(runner2?.odds);
    } else {
      // 2-runner market (e.g. cricket)
      const runner2 = findRunnerBySno(section, 3) || section[1];
      odds.two = extractOdds(runner2?.odds);
    }
  } else if (section.length === 1) {
    odds.one = extractOdds(section[0]?.odds);
  }

  const startDate = parseStime(match.stime);
  const timeStr = new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(startDate);

  const isLive = Boolean(match.iplay);
  // Check both match-level status and section-level gstatus
  const isSuspended =
    match.status === 'SUSPENDED' ||
    section.some((s) => s.gstatus === 'SUSPENDED');

  // Find draw runner name using sno
  const drawRunner = findRunnerBySno(section, 2);

  return {
    id: match.gmid || match.mid,
    matchId: match.gmid || match.mid,
    gmid: match.gmid,
    sid: sid,
    team1,
    team2,
    team3: runnerCount === 3 ? (drawRunner?.nat || 'The Draw') : '',
    runners: section.map((s) => ({
      runnerName: s.nat,
      selectionId: s.sid,
    })),
    status: isLive ? 'In-Play' : timeStr,
    isLive,
    isSuspended,
    time: timeStr,
    date: startDate,
    eventid: match.gmid,
    matchTitle: match.ename || `${team1} v ${team2}`,
    sportName,
    sportId: sid,
    competitionName: match.cname || '',
    odds,
    totalMatched: odds.totalMatched,
    hasTV: Boolean(match.tv),
    hasBookmaker: Boolean(match.bm),
    hasFancy: Boolean(match.f),
  };
};

export const transformListResponse = (apiResponse, sid) => {
  if (!apiResponse) return [];

  // Response shape: { list: { t1: [...matches...], t2: [...matches...] } }
  const t1 = apiResponse?.list?.t1;
  const t2 = apiResponse?.list?.t2;

  const allMatches = [
    ...(Array.isArray(t1) ? t1 : []),
    ...(Array.isArray(t2) ? t2 : []),
  ];

  if (allMatches.length === 0) return [];

  const sportName = SID_TO_NAME[sid] || 'Unknown';

  // Deduplicate by gmid (t2 may repeat matches from t1)
  const seen = new Set();
  const unique = allMatches.filter((m) => {
    const key = m.gmid || m.mid;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Exclude Virtual Cricket leagues and virtual/automated team matches
  // Patterns to block:
  //   cname: "Virtual Cricket League", "T10 XI", "T5 XI", "T12 XII", etc.
  //   ename: "Delhi Capitals T10 v Gujarat Titans T10", "SNP XI v GAW XI", etc.
  const virtualCnamePattern = /\bT\d+\s*X(?:I{1,2})\b/i;   // T5 XI, T10 XI, T12 XII…
  const t1t12EnamePattern   = /\bT([1-9]|1[0-2])\b/;         // T1–T12 in team names
  const xiXiiEnamePattern   = /\bX(?:I{1,2})\b/;             // XI or XII suffix in team names

  const filtered = unique.filter((m) => {
    const cname = m.cname || '';
    const ename = m.ename || '';
    if (cname.toLowerCase().includes('virtual')) return false;  // Virtual Cricket League
    if (virtualCnamePattern.test(cname)) return false;          // T10 XI, T5 XI, etc.
    if (t1t12EnamePattern.test(ename)) return false;            // T1–T12 in match name
    if (xiXiiEnamePattern.test(ename)) return false;            // XI / XII team suffix
    return true;
  });

  return filtered.map((match) => transformMatch(match, sid, sportName));
};

/**
 * Human-readable market name from API mname
 */
const MARKET_DISPLAY_NAMES = {
  MATCH_ODDS: 'Match Odds',
  Bookmaker: 'Bookmaker',
  OVER_UNDER_05: 'Over/Under 0.5 Goals',
  OVER_UNDER_15: 'Over/Under 1.5 Goals',
  OVER_UNDER_25: 'Over/Under 2.5 Goals',
  OVER_UNDER_35: 'Over/Under 3.5 Goals',
  BM_OVER_UNDER_05: 'BM Over/Under 0.5',
  BM_OVER_UNDER_15: 'BM Over/Under 1.5',
  DRAW_NO_BET: 'Draw No Bet',
  BOTH_TEAMS_TO_SCORE: 'Both Teams To Score',
  CORRECT_SCORE: 'Correct Score',
  '2nd Period Winner': '2nd Half Winner',
  'Next Goal': 'Next Goal',
  '1X2 Corners': '1X2 Corners',
  'Match Time Results': 'Match Time Results',
  'Match Result/Both Teams To Score': 'Match Result / BTTS',
  'Half Time/Full Time': 'Half Time / Full Time',
};

/**
 * Transform a single market's section into runner rows
 */
const transformMarketRunners = (section, matchData) => {
  if (!section || section.length === 0) return [];

  return section.map((runner) => {
    let teamName = runner.nat || '';
    let outcome = '';

    // Use sno for correct mapping: 1=team1, 2=draw, 3=team2
    if (section.length === 2) {
      if (runner.sno === 1) {
        outcome = 'one';
        teamName = runner.nat || matchData?.team1 || '';
      } else {
        outcome = 'two';
        teamName = runner.nat || matchData?.team2 || '';
      }
    } else if (section.length === 3) {
      if (runner.sno === 1) {
        teamName = runner.nat || matchData?.team1 || '';
        outcome = 'one';
      } else if (runner.sno === 2) {
        teamName = runner.nat || 'The Draw';
        outcome = 'x';
      } else if (runner.sno === 3) {
        teamName = runner.nat || matchData?.team2 || '';
        outcome = 'two';
      }
    }

    const { backOdds, layOdds } = extractDetailedOdds(runner.odds);

    return {
      team: teamName,
      outcome,
      backOdds,
      layOdds,
      gstatus: runner.gstatus || '',
      score: runner.score != null ? String(runner.score) : '0',
      sid: runner.sid,
      min: runner.min,
      max: runner.max,
      rawOdds: runner.odds || [],
    };
  });
};

/**
 * Extract the raw markets array from the API response
 */
const extractMarkets = (apiResponse) => {
  if (apiResponse?.data?.data && Array.isArray(apiResponse.data.data)) {
    return apiResponse.data.data;
  } else if (apiResponse?.data && Array.isArray(apiResponse.data)) {
    return apiResponse.data;
  } else if (Array.isArray(apiResponse)) {
    return apiResponse;
  }
  return null;
};

/**
 * Transform the get-sports-data-id API response to ALL markets format
 * Returns array of market objects, each with runners
 *
 * @param {Object} apiResponse - Raw API response
 * @param {Object} matchData - Match data with team names (for fallback)
 * @param {boolean} isMobile - Whether mobile view (affects odds count)
 * @returns {Array|null} Array of market objects: [{ marketName, mname, isSuspended, runners: [...] }]
 */
export const transformDetailResponse = (apiResponse, matchData, isMobile = false) => {
  const markets = extractMarkets(apiResponse);
  if (!markets || markets.length === 0) return null;

  const result = [];

  for (const market of markets) {
    if (!market?.section || market.section.length === 0) continue;

    const mname = market.mname || 'Unknown';
    const displayName = MARKET_DISPLAY_NAMES[mname] || mname.replace(/_/g, ' ');
    const isSuspended = market.section.some((s) => s.gstatus !== 'ACTIVE');
    const runners = transformMarketRunners(market.section, matchData);

    result.push({
      marketName: displayName,
      mname,
      sno: market.sno,
      gtype: market.gtype,
      mid: market.mid,
      status: market.status || 'OPEN',
      maxb: market.maxb,
      rem: market.rem || '',
      iplay: market.iplay,
      biplay: market.biplay,
      isSuspended,
      min: market.min,
      max: market.max,
      rawSection: market.section,
      runners,
    });
  }

  return result.length > 0 ? result : null;
};
